<script setup>
import Footer from './FooterSection.vue'
import Breadcrumbs from './BreadCrumbs.vue'
import BcsDynamicText from './BcsDynamicText.vue'
import { useFaqsStore } from '../stores/FaqsStore'
import { useAppCapabilities } from '../composables/useAppCapabilities'
import { useAuthStore } from '../stores/AuthStore'
import { onMounted, ref, computed, inject, nextTick } from 'vue'
import TextWithDefinitions from './TextWithDefinitions.vue'
import BcsSearchDropdown from './BcsSearchDropdown.vue'
import BcsAccordion from './BcsAccordion.vue'

const faqStore = useFaqsStore()
const authStore = useAuthStore()
const { getFaqs } = faqStore
const { hasCoreWorkbench, hasContentLibrary, hasTransform } = useAppCapabilities()

/*
 * Every section is gated by the module it belongs to: the two module sections by their own
 * permission, and all the rest (General, Action Center, the compliance topics…) by the core
 * workbench. A core user used to get a free pass over the whole page, which is what showed
 * the Content Library and BCS Transform questions to a core-only persona.
 */
const MODULE_SECTION_ACCESS = {
  contentLibrary: hasContentLibrary,
  transform: hasTransform
}

const isSectionVisible = (sectionId) => {
  const moduleAccess = MODULE_SECTION_ACCESS[sectionId]
  return moduleAccess ? moduleAccess.value : hasCoreWorkbench.value
}
const customNavigation = inject('customNavigation')
const faqSections = ref([])
const displayedFaqSections = ref([])
const flattenedFaqs = ref([])

const scrollToFaqTarget = async () => {
  const faqScrollTarget = customNavigation.value.navigationParams.faqScrollTarget

  if (!faqScrollTarget) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  await nextTick()

  const targetElement = document.getElementById(faqScrollTarget)
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  customNavigation.value.navigationParams.faqScrollTarget = null
}

onMounted(async () => {
  await authStore.ensureAccessLoaded()
  const remoteFaqs = await getFaqs()

  if (remoteFaqs.value) {
    const allSections = Object.entries(remoteFaqs.value).map(([key, section]) => ({
      ...section,
      questionTitle: key,
      FaqList: section.FaqList ? JSON.parse(section.FaqList) : []
    }))
    faqSections.value = allSections.filter(section => isSectionVisible(section.sectionId))
    displayedFaqSections.value = [...faqSections.value]
    flattenedFaqs.value = faqSections.value.flatMap(section =>
      section.FaqList.map(faq => ({
        ...faq,
        sectionId: section.sectionId
      }))
    )
  }
  await scrollToFaqTarget()
})

const accordionSections = computed(() => {
  return displayedFaqSections.value.map(section => ({
    id: section.sectionId,
    title: section.sectionName,
    items: section.FaqList.map(faq => ({
      id: faq.MIGX_id,
      title: faq.faqTitle,
      content: faq.faqBody,
      MIGX_id: faq.MIGX_id,
      sectionId: section.sectionId
    }))
  }))
})

const handleSearch = ({ results }) => {
  if (results.length === 0) {
    displayedFaqSections.value = []
    return
  }
  const resultsBySection = {}
  results.forEach(result => {
    if (!resultsBySection[result.sectionId]) {
      resultsBySection[result.sectionId] = []
    }
    resultsBySection[result.sectionId].push(result)
  })

  displayedFaqSections.value = Object.keys(resultsBySection).map(sectionId => {
    const originalSection = faqSections.value.find(s => s.sectionId === sectionId)
    const section = originalSection || {
      sectionId: sectionId,
      sectionName: sectionId.charAt(0).toUpperCase() + sectionId.slice(1),
      MIGX_id: "search-" + sectionId,
      questionTitle: "Search Results"
    }
    const matchingFaqs = resultsBySection[sectionId];
    return {
      ...section,
      FaqList: matchingFaqs
    }
  })
}

const handleSelect = (faq) => {
  const section = displayedFaqSections.value.find(s => s.sectionId === faq.sectionId)
  if (section) {
    const targetFaq = section.FaqList.find(f => f.MIGX_id === faq.MIGX_id)
    if (targetFaq) {
      // The accordion will handle the selection
    }
  }
}

const handleClear = () => {
  displayedFaqSections.value = [...faqSections.value]
}
</script>

<template>
  <div class="bcs-faqs">
    <div class="mt-4 bcs-page-padding">
      <div class="row">
        <div class="col-6">
          <Breadcrumbs></Breadcrumbs>
          <p class="text-28">
            <med>Frequently Asked Questions</med>
          </p>
          <p class="text-14">
            <BcsDynamicText text="faqsPageIntro" />
          </p>

          <div style="margin-bottom: 50px;">

            <BcsSearchDropdown placeholder="Search FAQs by keyword or sentence" :items="flattenedFaqs"
              :searchFields="['faqTitle', 'faqBody']" :maxSuggestions="10" @search="handleSearch"
              :arrayTerm="'faqTitle'" @select="handleSelect" @clear="handleClear" />
          </div>

          <BcsAccordion :sections="accordionSections" sectionTitleClass="header-22" itemTitleClass="text-primary"
            itemContentClass="text-14">
            <template #item-content="{ item }">
              <TextWithDefinitions :key="item.content" :text="item.content" />
            </template>
          </BcsAccordion>
        </div>
      </div>
    </div>

    <Footer></Footer>

  </div>
</template>


<style scoped lang="scss">
.bcs-faqs {
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.text-14,
.header-22 {
  margin-bottom: 30px;
}

:deep {
  .search-container {
    input {
      color: var(--bs-primary);
    }
  }
}
</style>
